import { GetServerSideProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { supabaseAdmin } from '../../lib/supabaseServer';
import { getCjSyncSettings } from '../../services/firebase';
import { isMedusaBackendEnabled } from '../../lib/featureFlags';
import { medusaFetch } from '../../lib/medusaClient';
import SEOHead from '../../components/SEOHead';
import { useCart } from '../../lib/CartContext';
import customToast from '../../lib/toastUtils';
import { FiShoppingCart, FiArrowLeft, FiTruck, FiPackage, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

type Variant = { vid: string; variantNameEn: string; variantSellPrice: number; variantImage?: string; variantStock: number; variantSku: string; variantWeight?: number };

type ProductPageProps = {
  product: {
    id: string;
    slug: string;
    title: string;
    imageUrl: string;
    images: string[];
    price: number;
    discountPercent: number | null;
    categoryId: string;
    categoryName: string;
    categorySlug: string;
    categoryBreadcrumb?: string;
    stock: number;
    description?: string;
    material?: string | null;
    specification?: string | null;
    productAttributes?: Record<string, string>;
    cjPid?: string;
    variants?: Variant[];
    weight: number | null;
  } | null;
};

const formatPrice = (p: number) =>
  `€${p.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const UUID_REGEX = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;

type MedusaVariant = {
  id?: string;
  metadata?: Record<string, unknown>;
  title?: string;
  sku?: string;
  inventory_quantity?: number;
  calculated_price?: { calculated_amount?: number };
  prices?: Array<{ amount?: number }>;
  options?: Array<{ value?: string }>;
  weight?: number;
};

type MedusaProduct = {
  id?: string;
  handle?: string;
  title?: string;
  description?: string;
  thumbnail?: string;
  images?: Array<{ url?: string }>;
  status?: string;
  categories?: Array<{ id?: string; name?: string; handle?: string }>;
  collection?: { id?: string; title?: string; handle?: string };
  variants?: MedusaVariant[];
  metadata?: Record<string, unknown>;
};

function toNonNegativeNumber(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

function resolveMedusaPrice(variant: MedusaVariant): number {
  const calculated = Number(variant?.calculated_price?.calculated_amount ?? 0);
  if (calculated > 0) return calculated / 100;
  const first = Number(variant?.prices?.[0]?.amount ?? 0);
  if (first > 0) return first / 100;
  return 0;
}

function mapMedusaProductToPageProduct(product: MedusaProduct): ProductPageProps['product'] {
  const categories = Array.isArray(product.categories) ? product.categories : [];
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const firstCategory = categories[0];

  const categoryId = String(firstCategory?.id || product.collection?.id || 'general');
  const categoryName = String(firstCategory?.name || product.collection?.title || 'General');
  const categorySlug = String(firstCategory?.handle || product.collection?.handle || 'general');

  const mappedVariants: Variant[] = variants
    .map((variant) => {
      const variantId = String(variant.id || '').trim();
      if (!variantId) return null;
      const optionValue = variant.options?.map((o) => o?.value).filter(Boolean).join(' / ');
      const title = String(optionValue || variant.title || 'Default');
      return {
        vid: variantId,
        variantNameEn: title,
        variantSellPrice: resolveMedusaPrice(variant),
        variantStock: Math.max(
          0,
          Number(
            variant.inventory_quantity ??
              variant.metadata?.legacy_stock ??
              0
          )
        ),
        variantSku: String(variant.sku || ''),
        ...(typeof variant.weight === 'number' ? { variantWeight: variant.weight } : {}),
      };
    })
    .filter((variant): variant is Variant => Boolean(variant));

  const variantStock = mappedVariants.reduce((sum, variant) => sum + Math.max(0, Number(variant.variantStock || 0)), 0);
  const productFallbackStock = toNonNegativeNumber(product.metadata?.legacy_stock);
  const stock = Math.max(0, (variantStock > 0 ? variantStock : productFallbackStock) - 2);
  const imageUrls = (product.images || [])
    .map((image) => String(image?.url || '').trim())
    .filter((value) => value.length > 0);

  const firstVariantPrice = mappedVariants.find((variant) => variant.variantSellPrice > 0)?.variantSellPrice ?? 0;

  return {
    id: String(product.id || ''),
    slug: String(product.handle || ''),
    title: String(product.title || 'Product'),
    imageUrl: String(product.thumbnail || imageUrls[0] || '/no-image.png'),
    images: imageUrls.length > 0 ? imageUrls : [String(product.thumbnail || '/no-image.png')],
    price: firstVariantPrice,
    discountPercent: null,
    categoryId,
    categoryName,
    categorySlug,
    categoryBreadcrumb: categoryName,
    stock,
    description: String(product.description || ''),
    material: null,
    specification: null,
    productAttributes: {},
    cjPid: String((product.metadata?.cj_product_id as string) || product.id || ''),
    variants: mappedVariants,
    weight:
      typeof mappedVariants[0]?.variantWeight === 'number'
        ? mappedVariants[0].variantWeight
        : null,
  };
}

async function fetchMedusaProductBySlugOrId(slugOrId: string): Promise<ProductPageProps['product']> {
  const fields = '*variants,+variants.calculated_price,+variants.inventory_quantity,+variants.options,+variants.weight,+variants.metadata,+categories,+collection,+metadata';

  let product: MedusaProduct | null = null;
  if (slugOrId.startsWith('prod_')) {
    const byId = await medusaFetch<{ product?: MedusaProduct }>(`/store/products/${encodeURIComponent(slugOrId)}?fields=${encodeURIComponent(fields)}`);
    product = byId.product || null;
  } else {
    const byHandle = await medusaFetch<{ products?: MedusaProduct[] }>(`/store/products?handle=${encodeURIComponent(slugOrId)}&limit=1&fields=${encodeURIComponent(fields)}`);
    product = Array.isArray(byHandle.products) ? byHandle.products[0] || null : null;
  }

  if (!product) return null;
  if (product.status && product.status !== 'published') return null;

  const mapped = mapMedusaProductToPageProduct(product);
  if (!mapped || mapped.stock < 1) return null;
  return mapped;
}

const safeImage = (src: any): string => {
  if (typeof src === 'string') {
    const trimmed = src.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') {
          return parsed[0];
        }
      } catch {
        // ignore parse errors
      }
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return trimmed;
    }
  }
  return '/no-image.png';
};

const extractImages = (html: string): string[] => {
  if (!html) return [];
  const regex = /<img[^>]+src=["']([^"']+)["'][^>]*>/g;
  const images: string[] = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    images.push(match[1]);
  }
  return images;
};

const cleanDescriptionHTML = (html: string): string => {
  if (!html) return html;
  // Remove common product labels that we display separately
  return html
    .replace(/<p>\s*<strong>\s*Product Image:\s*<\/strong>\s*<\/p>/gi, '')
    .replace(/<p>\s*Product Image:\s*<\/p>/gi, '')
    .replace(/<strong>\s*Product Image:\s*<\/strong>/gi, '')
    .replace(/Product Image:/gi, '')
    .trim();
};

/**
 * CJ returns variantNameEn as the full product title + the variant attribute
 * appended (e.g. "...Price Tag White USB"). Find the longest common word-prefix
 * shared by ALL variants and strip it, leaving only the unique part.
 */
function buildVariantLabels(variants: Variant[]): Record<string, string> {
  if (!variants || variants.length === 0) return {};
  if (variants.length === 1) return { [variants[0].vid]: variants[0].variantNameEn };

  const names = variants.map((v) => v.variantNameEn);
  const words0 = names[0].split(/\s+/);
  let commonCount = words0.length;
  for (const name of names.slice(1)) {
    const words = name.split(/\s+/);
    let i = 0;
    while (i < commonCount && i < words.length && words0[i] === words[i]) i++;
    commonCount = i;
  }

  const result: Record<string, string> = {};
  for (const v of variants) {
    const words = v.variantNameEn.split(/\s+/);
    const unique = words.slice(commonCount).join(' ').trim();
    // If nothing is unique (e.g. only 1 variant or all identical) fall back to full name
    result[v.vid] = unique || v.variantNameEn;
  }
  return result;
}

export default function ProductPage({ product }: ProductPageProps) {
  const router = useRouter();
  const { addProduct } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [descriptionImageIndex, setDescriptionImageIndex] = useState(0);
  const [specificationImageIndex, setSpecificationImageIndex] = useState(0);
  const isLt = router.asPath.startsWith('/lt');
  const shopHref = isLt ? '/lt/parduotuve' : '/shop';
  const baseImageUrl = safeImage(product?.imageUrl ?? '');
  const rawImages: any = (product as any)?.images;
  const images: string[] = Array.isArray(rawImages) && rawImages.length
    ? rawImages.map(safeImage).filter(Boolean)
    : baseImageUrl
      ? [baseImageUrl]
      : [];

  // Extract images from description and specification
  const descriptionImages = product?.description ? extractImages(product.description) : [];
  const specificationImages = product?.specification ? extractImages(product.specification) : [];
  const variantLabels = buildVariantLabels(product?.variants ?? []);

  if (!product) {
    return (
      <>
        <SEOHead title={isLt ? 'Produktas nerastas' : 'Product not found'} description={isLt ? 'Produktas nerastas' : 'Product not found'} noIndex />
        <div className="min-h-screen bg-white dark:bg-black flex flex-col items-center justify-center px-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {isLt ? 'Produktas nerastas' : 'Product not found'}
          </h1>
          <Link
            href={shopHref}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primaryDark text-white font-semibold transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            {isLt ? 'Atgal į parduotuvę' : 'Back to shop'}
          </Link>
        </div>
      </>
    );
  }

  const selectedVariant =
    product.variants?.length && selectedVariantId
      ? product.variants.find((v) => v.vid === selectedVariantId)
      : null;
  const displayPrice = selectedVariant != null ? selectedVariant.variantSellPrice : product.price;
  const discount = product.discountPercent ?? 0;
  const finalPrice = discount > 0 ? displayPrice * (1 - discount / 100) : displayPrice;
  const mainImageUrl =
    selectedVariant?.variantImage
      ? safeImage(selectedVariant.variantImage)
      : (images[selectedImage] ?? baseImageUrl);
  const routeId = Array.isArray(router.query.id) ? router.query.id[0] : router.query.id;
  const canonicalProductPath = `/product/${product.slug || routeId || product.id}`;
  // Prefer per-variant stock when available; otherwise fall back to product-level stock.
  const rawSelectedVariantStock =
    selectedVariant && typeof (selectedVariant as any).variantStock === 'number'
      ? (selectedVariant as any).variantStock
      : null;
  const selectedVariantStock =
    rawSelectedVariantStock != null && rawSelectedVariantStock >= 0
      ? rawSelectedVariantStock
      : null;
  const effectiveStock =
    selectedVariantStock != null && selectedVariantStock > 0
      ? selectedVariantStock
      : product.stock;

  const hasVariants = (product.variants?.length ?? 0) > 0;
  const optionRequiredButMissing = hasVariants && !selectedVariantId;
  const outOfStock = effectiveStock < 1;
  const canAddToCart = !optionRequiredButMissing && !outOfStock;

  const handleAddToCart = () => {
    if (!canAddToCart) return;
    addProduct(
      {
        id: product.id,
        title: product.title,
        price: finalPrice.toFixed(2),
        imageUrl: mainImageUrl,
        mode: 'payment',
        stripePriceId: product.id,
        ...(selectedVariant && {
          variantId: selectedVariant.vid,
          variantLabel: selectedVariant.variantNameEn,
        }),
      },
      quantity
    );
    customToast.success(isLt ? 'Pridėta į krepšelį!' : 'Added to cart!', 'added-to-cart');
  };

  return (
    <>
      <SEOHead
        title={`${product.title} | GadgetFever`}
        description={product.description || product.title}
        canonicalUrl={canonicalProductPath}
      />
      <div className="min-h-screen bg-white dark:bg-black overflow-x-hidden">
        <div className="w-full max-w-6xl mx-auto px-4 py-4 md:py-6">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-3 md:mb-6">
            <Link href={isLt ? '/lt/' : '/'} className="hover:text-primary dark:hover:text-sand-400">{isLt ? 'Pagrindinis' : 'Home'}</Link>
            <span>/</span>
            <Link href={shopHref} className="hover:text-primary dark:hover:text-sand-400">{isLt ? 'Parduotuvė' : 'Shop'}</Link>
            {product.categoryName && (
              <>
                <span>/</span>
                <span className="text-gray-700 dark:text-gray-300">{product.categoryName}</span>
              </>
            )}
          </nav>

          <Link
            href={shopHref}
            className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-sand-400 mb-4 md:mb-6"
          >
            <FiArrowLeft className="w-3.5 h-3.5 md:w-4 md:h-4" />
            {isLt ? 'Atgal į parduotuvę' : 'Back to shop'}
          </Link>

          <div className="w-full bg-white dark:bg-black rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2 gap-4 md:gap-8 p-4 md:p-8 md:items-start">
              {/* Image gallery */}
              <div className="min-w-0 flex flex-col gap-2 md:gap-3 md:sticky md:top-4">
                <div className="relative w-full bg-gray-100 dark:bg-neutral-900 rounded-xl overflow-hidden flex items-center justify-center" style={{minHeight: '260px'}}>
                  {mainImageUrl ? (
                    <Image
                      src={mainImageUrl}
                      alt={product.title}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className="object-contain p-3"
                      priority
                    />
                  ) : (
                    <div className="flex items-center justify-center text-4xl md:text-6xl" style={{height: '260px'}}>📦</div>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {images.map((url, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelectedImage(i)}
                        className={`relative flex-shrink-0 w-16 h-16 md:w-14 md:h-14 rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedImage === i ? 'border-primary dark:border-sand-400' : 'border-gray-200 dark:border-neutral-700'
                        }`}
                      >
                        <Image src={url} alt={`${product.title} thumbnail ${i + 1}`} fill className="object-cover" sizes="64px" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="min-w-0 flex flex-col">
                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1">{product.categoryName}</p>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4 leading-tight break-words">
                  {product.title}
                </h1>
                <div className="mb-3 md:mb-4">
                  {discount > 0 ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base md:text-lg text-gray-400 dark:text-gray-500 line-through">
                        {formatPrice(displayPrice)}
                      </span>
                      <span className="text-xl md:text-2xl font-bold text-primary dark:text-sand-400">
                        {formatPrice(finalPrice)}
                      </span>
                      <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                        ({discount}% off)
                      </span>
                    </div>
                  ) : (
                    <span className="text-xl md:text-2xl font-bold text-primary dark:text-sand-400">
                      {formatPrice(displayPrice)}
                    </span>
                  )}
                </div>

                {product.variants && product.variants.length > 0 && (
                  <div className="mb-3 md:mb-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {isLt ? 'Pasirinkite variantą' : 'Options'}
                      <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">({product.variants.length})</span>
                    </p>
                    <div
                      className="grid grid-cols-2 gap-1.5 overflow-y-auto pr-0.5 scrollbar-thin"
                      style={{ maxHeight: product.variants.length > 8 ? '260px' : 'none' }}
                    >
                      {product.variants.map((v) => {
                        const selected = selectedVariantId === v.vid;
                        const rawStock =
                          typeof (v as any).variantStock === 'number'
                            ? (v as any).variantStock
                            : null;
                        const vStock =
                          rawStock != null && rawStock >= 0
                            ? rawStock
                            : product.stock;
                        const disabled = (vStock ?? 0) < 1;
                        return (
                          <button
                            key={v.vid}
                            type="button"
                            onClick={() => !disabled && setSelectedVariantId(v.vid)}
                            className={`w-full px-2.5 py-2 rounded-lg border-2 text-left text-xs transition-colors ${
                              disabled
                                ? 'border-gray-200 dark:border-neutral-800 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                                : selected
                                ? 'border-primary bg-primary/10 text-primary dark:bg-sand-400/10 dark:text-sand-400 dark:border-sand-400'
                                : 'border-gray-200 dark:border-neutral-700 hover:border-primary/50 dark:hover:border-sand-400/50 text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            <div className="font-medium truncate">
                              {variantLabels[v.vid] || v.variantNameEn}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 mt-0.5 flex items-center justify-between gap-1">
                              <span>{formatPrice(v.variantSellPrice)}</span>
                              {vStock != null && (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                  {vStock < 1
                                    ? (isLt ? 'Nėra sandėlyje' : 'Out of stock')
                                    : `${vStock} ${isLt ? 'vnt.' : 'pcs'}`}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {product.variants.length > 8 && (
                      <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500 text-center">↕ {isLt ? 'Slinkti variantus' : 'Scroll to see all options'}</p>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-3 md:mb-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{isLt ? 'Kiekis' : 'Quantity'}</label>
                  <div className="flex items-center border border-gray-300 dark:border-neutral-700 rounded-lg overflow-hidden w-fit">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800 active:bg-gray-200 dark:active:bg-neutral-700"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={effectiveStock}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(effectiveStock, parseInt(e.target.value, 10) || 1)))}
                      className="w-14 text-center border-0 border-x border-gray-300 dark:border-neutral-700 bg-transparent text-gray-900 dark:text-white text-base number-input-no-arrows"
                    />
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(effectiveStock, q + 1))}
                      className="w-10 h-10 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800 active:bg-gray-200 dark:active:bg-neutral-700"
                    >
                      +
                    </button>
                  </div>
            </div>

                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-3 md:mb-4">
                  {isLt ? 'Turime sandelyje:' : 'In stock:'}{' '}
                  <span className="font-semibold text-gray-800 dark:text-white">{effectiveStock}</span>
                  {(selectedVariant?.variantWeight ?? product.weight) != null &&
                    (selectedVariant?.variantWeight ?? product.weight)! > 0 && (
                    <span className="ml-2"> • {(selectedVariant?.variantWeight ?? product.weight)}g</span>
                  )}
                </p>

                {optionRequiredButMissing && (
                  <p className="text-xs md:text-sm text-amber-600 dark:text-amber-400 mb-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    {isLt ? 'Pasirinkite variantą (pvz. spalvą ar dydį)' : 'Please select an option (e.g. size or color)'}
                  </p>
                )}
                {hasVariants && selectedVariantId && outOfStock && (
                  <p className="text-xs md:text-sm text-red-600 dark:text-red-400 mb-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    {isLt ? 'Šis variantas laikinai nepasiekiamas' : 'This option is currently out of stock'}
                  </p>
                )}
                <button
                  onClick={handleAddToCart}
                  disabled={!canAddToCart}
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-xl bg-primary hover:bg-primaryDark disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors text-base md:text-base shadow-lg active:scale-[0.98]"
                >
                  <FiShoppingCart className="w-5 h-5" />
                  {isLt ? 'Į krepšelį' : 'Add to cart'}
                </button>

                {/* Processing & delivery — same for all products */}
                <div className="mt-4 md:mt-6 p-3 md:p-4 rounded-xl bg-gray-50 dark:bg-neutral-900/80 border border-gray-200 dark:border-neutral-800 space-y-1.5 md:space-y-2">
                  <p className="flex items-center gap-2 text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200">
                    <FiPackage className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                    {isLt ? 'Apdorojimas ir pristatymas' : 'Processing & delivery'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    <strong>{isLt ? 'Numatomas apdorojimo laikas:' : 'Estimated Processing Time:'}</strong>{' '}
                    {isLt ? '1–3 dienos 90% užsakymų' : '1–3 days for 90% of orders'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    <strong>{isLt ? 'Numatomas pristatymo laikas:' : 'Estimated Delivery Time:'}</strong>{' '}
                    {isLt ? '8–30 dienų' : '8–30 days'}
                  </p>
                </div>

                </div>
            </div>

            {/* Description section — full width below */}
            <div className="border-t border-gray-200 dark:border-neutral-800 p-6 md:p-8">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                {isLt ? 'Aprašymas' : 'Description'}
              </h2>

              <div className="grid md:grid-cols-2 gap-4 md:gap-8">
                {/* Left side: Product information text */}
                <div className="space-y-3 description-scroll">
                  {product.description ? (
                    <div
                      className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed product-description-content"
                      dangerouslySetInnerHTML={{ __html: cleanDescriptionHTML(product.description) }}
                    />
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-500 italic">
                      {isLt ? 'Aprašymas nepateiktas.' : 'No description provided.'}
                    </p>
                  )}

                  {/* SKU */}
                  {product.cjPid && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        SKU: <span className="font-normal text-gray-600 dark:text-gray-400">{product.cjPid}</span>
                      </p>
                    </div>
                  )}

                  {/* Material and Package info */}
                  {product.material && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {isLt ? 'Medžiaga:' : 'Material:'} <span className="font-normal text-gray-600 dark:text-gray-400">
                          {(() => {
                            const raw = product.material as any;
                            try {
                              if (typeof raw === 'string' && raw.trim().startsWith('[')) {
                                const arr = JSON.parse(raw.trim());
                                if (Array.isArray(arr)) return arr.join(', ');
                              }
                            } catch {}
                            return String(raw);
                          })()}
                        </span>
                      </p>
                    </div>
                  )}
                  {product.productAttributes && Object.keys(product.productAttributes).length > 0 && (
                    <div className="space-y-1">
                      {Object.entries(product.productAttributes).slice(0, 5).map(([key, value]) => {
                        let text = '';
                        const raw: any = value;
                        try {
                          if (Array.isArray(raw)) {
                            text = raw.join(', ');
                          } else if (typeof raw === 'string' && raw.trim().startsWith('[')) {
                            const arr = JSON.parse(raw.trim());
                            text = Array.isArray(arr) ? arr.join(', ') : String(raw);
                          } else {
                            text = String(raw);
                          }
                        } catch {
                          text = String(raw);
                        }
                        return (
                          <p key={key} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {key}: <span className="font-normal text-gray-600 dark:text-gray-400">{text}</span>
                          </p>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Right side: Product Image carousel */}
                {descriptionImages.length > 0 && (
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 md:mb-4 text-center w-full">
                      {isLt ? 'Produkto vaizdas:' : 'Product Image:'}
                    </p>
                    <div className="flex items-center gap-2 md:gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setDescriptionImageIndex((i) =>
                            i === 0 ? descriptionImages.length - 1 : i - 1
                          )
                        }
                        className="p-1.5 md:p-2 hover:bg-gray-200 dark:hover:bg-neutral-800 rounded transition-colors flex-shrink-0"
                      >
                        <FiChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
                      </button>
                      <div className="relative w-64 h-64 md:w-[420px] md:h-[420px] flex-shrink-0 bg-white dark:bg-neutral-900 rounded-xl border-2 border-gray-300 dark:border-neutral-700 p-2 md:p-4">
                        <Image
                          src={descriptionImages[descriptionImageIndex]}
                          alt="description"
                          fill
                          className="object-contain p-1 md:p-2"
                          sizes="(max-width: 768px) 256px, 420px"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setDescriptionImageIndex((i) =>
                            (i + 1) % descriptionImages.length
                          )
                        }
                        className="p-1.5 md:p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                      >
                        <FiChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
                      </button>
                    </div>
                    <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-2 md:mt-3">
                      {descriptionImageIndex + 1}/{descriptionImages.length}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* Specification */}
            {product.specification && product.specification !== product.description && (
              <div className="border-t border-gray-200 dark:border-neutral-800 p-6 md:p-8">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                  {isLt ? 'Specifikacija' : 'Specification'}
                </h2>

                {/* Specification image carousel */}
                {specificationImages.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 w-fit">
                    <button
                      type="button"
                      onClick={() =>
                        setSpecificationImageIndex((i) =>
                          i === 0 ? specificationImages.length - 1 : i - 1
                        )
                      }
                      className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                    >
                      <FiChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                    <div className="relative w-[120px] h-[120px] flex-shrink-0 bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-700">
                      <Image
                        src={specificationImages[specificationImageIndex]}
                        alt="specification"
                        fill
                        className="object-contain p-2"
                        sizes="120px"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setSpecificationImageIndex((i) =>
                          (i + 1) % specificationImages.length
                        )
                      }
                      className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                    >
                      <FiChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                      {specificationImageIndex + 1}/{specificationImages.length}
                    </span>
                  </div>
                )}

                <div
                  className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed product-description-content"
                  dangerouslySetInnerHTML={{ __html: cleanDescriptionHTML(product.specification) }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async (ctx) => {
  const slugOrId = ctx.params?.id as string | undefined;
  if (!slugOrId) {
    return { notFound: true };
  }

  if (isMedusaBackendEnabled()) {
    try {
      const medusaProduct = await fetchMedusaProductBySlugOrId(slugOrId);
      if (medusaProduct) {
        return { props: { product: medusaProduct } };
      }
    } catch (error) {
      console.error('[product] Medusa SSR fetch failed, falling back to legacy Supabase', error);
    }
  }

  try {
    let d: any = null;
    let docId = '';

    if (UUID_REGEX.test(slugOrId)) {
      const { data } = await supabaseAdmin.from('products').select('*').eq('id', slugOrId).single();
      if (data) {
        d = data;
        docId = data.id;
      }
    }
    if (!d) {
      const { data } = await supabaseAdmin.from('products').select('*').eq('slug', slugOrId).limit(1).single();
      if (data) {
        d = data;
        docId = data.id;
      }
    }

    if (!d) {
      return { props: { product: null } };
    }

    // Normalize snake_case from DB to camelCase for existing logic
    const dNorm = {
      ...d,
      imageUrl: d.image_url ?? d.imageUrl,
      categoryId: d.category_id ?? d.categoryId,
      categoryName: d.category_name ?? d.categoryName,
      categorySlug: d.category_slug ?? d.categorySlug,
      cjPid: d.cj_pid ?? d.cjPid ?? d.id,
    };
    const [settings] = await Promise.all([getCjSyncSettings()]);
    const marginType = settings.marginType ?? 'percent';
    const marginVal = typeof settings.marginValue === 'number' && settings.marginValue >= 0 ? settings.marginValue : 0;
    let price = Number(dNorm.price ?? 0);
    if (marginVal > 0) {
      price = marginType === 'fixed' ? price + marginVal : price * (1 + marginVal / 100);
    }

    const variantsStock = Array.isArray(dNorm.variants)
      ? (dNorm.variants as any[]).reduce((sum: number, v: any) => sum + (Number(v?.variantStock ?? v?.stock) || 0), 0)
      : 0;
    const stock = Math.max(0, Math.max(Number(dNorm.stock ?? 0), variantsStock) - 2);
    const active = dNorm.active !== false || variantsStock > 0;

    if (!active || stock < 1 || dNorm.is_shippable === false) {
      return { props: { product: null } };
    }

    const images = Array.isArray(dNorm.images) && dNorm.images.length > 0 ? dNorm.images : (dNorm.imageUrl ? [dNorm.imageUrl] : []);
    const variants = (dNorm.variants as any[])?.map((v: any) => {
      const base: any = {
        vid: String(v.vid ?? v.id ?? ''),
        variantNameEn: String(v.variantNameEn ?? v.variantName ?? v.name ?? ''),
        variantSellPrice:
          marginVal > 0
            ? marginType === 'fixed'
              ? Number(v.variantSellPrice ?? v.price ?? 0) + marginVal
              : Number(v.variantSellPrice ?? v.price ?? 0) * (1 + marginVal / 100)
            : Number(v.variantSellPrice ?? v.price ?? 0),
        variantImage: v.variantImage ?? v.image,
        variantStock: Number(v.variantStock ?? v.stock ?? 0),
        variantSku: String(v.variantSku ?? v.sku ?? ''),
      };
      if (typeof v.variantWeight === 'number' && v.variantWeight > 0) {
        base.variantWeight = v.variantWeight;
      }
      return base;
    }).filter((v: Variant) => v.vid);

    const discountPercent =
      dNorm.discount_percent != null ? Number(dNorm.discount_percent) : null;
    const material = (dNorm.material ?? dNorm.material_name_en ?? '') as string;
    const specification = (dNorm.specification ?? '') as string;
    const productAttributes =
      (dNorm.product_attributes != null && typeof dNorm.product_attributes === 'object')
        ? (dNorm.product_attributes as Record<string, string>)
        : undefined;

    const product = {
      id: docId,
      slug: (dNorm.slug ?? '') as string,
      title: (dNorm.title ?? '') as string,
      imageUrl: (dNorm.imageUrl ?? (images[0] ?? '')) as string,
      images: images as string[],
      price,
      discountPercent,
      categoryId: (dNorm.categoryId ?? '') as string,
      categoryName: (dNorm.categoryName ?? '') as string,
      categorySlug: (dNorm.categorySlug ?? dNorm.categoryId ?? '') as string,
      categoryBreadcrumb: (dNorm.categoryName ?? '') as string,
      stock,
      description: (dNorm.description ?? '') as string | undefined,
      material,
      specification,
      productAttributes: productAttributes && Object.keys(productAttributes).length > 0 ? productAttributes : {},
      cjPid: (dNorm.cjPid ?? docId) as string,
      variants: variants as Variant[],
      weight:
        typeof dNorm.productWeight === 'number'
          ? dNorm.productWeight
          : typeof dNorm.weight === 'number'
            ? dNorm.weight
            : null,
    };

    return { props: { product } };
  } catch {
    return { props: { product: null } };
  }
};
